export type CvProcessingFailureKind =
  | 'TEMPORARY_RETRY'
  | 'REPLACEMENT_REQUIRED_UNREADABLE'
  | 'REPLACEMENT_REQUIRED_NOT_CV';

export type CvProcessingFailureInput = {
  code?: string | null;
  retryable?: boolean | null;
  documentTypeValid?: boolean | null;
  applicationStatus?: string | null;
};

export type CvProcessingFailureApplication = {
  status?: string | null;
  cv_parsed_json?: unknown;
  cv_scores?: { breakdown_json?: unknown } | null;
};

const TEMPORARY_CODES = new Set([
  'FORK_INHOUSE_VISION_OCR_REQUIRED',
  'CV_STRUCTURE_FAILED',
  'CV_SCORING_FAILED',
]);

const UNREADABLE_CODES = new Set([
  'CV_PARSE_FAILED',
  'CV_PARSE_POOR_QUALITY',
]);

export function classifyCvProcessingFailure({
  code,
  retryable,
  documentTypeValid,
  applicationStatus,
}: CvProcessingFailureInput): CvProcessingFailureKind | null {
  if (documentTypeValid === false || code === 'CV_DOCUMENT_INVALID') {
    return 'REPLACEMENT_REQUIRED_NOT_CV';
  }

  if (UNREADABLE_CODES.has(code || '') || applicationStatus === 'rejected_unreadable') {
    return 'REPLACEMENT_REQUIRED_UNREADABLE';
  }

  if (retryable === true || TEMPORARY_CODES.has(code || '') || applicationStatus === 'cv_scoring_failed') {
    return 'TEMPORARY_RETRY';
  }

  return null;
}

export function cvProcessingFailureTranslationBase(kind: CvProcessingFailureKind): string {
  if (kind === 'TEMPORARY_RETRY') return 'hire.cvProcessingFailures.temporaryRetry';
  if (kind === 'REPLACEMENT_REQUIRED_NOT_CV') return 'hire.cvProcessingFailures.notCv';
  return 'hire.cvProcessingFailures.unreadable';
}

export function readPersistedCvProcessingFailure(value: unknown): CvProcessingFailureInput {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const metadata = (value as Record<string, unknown>)._cv_processing_failure;
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) return {};
  const row = metadata as Record<string, unknown>;
  return {
    code: typeof row.code === 'string' ? row.code : null,
    retryable: typeof row.retryable === 'boolean' ? row.retryable : null,
    documentTypeValid: typeof row.document_type_valid === 'boolean' ? row.document_type_valid : null,
  };
}

export function getApplicationCvProcessingFailure(
  application: CvProcessingFailureApplication | null | undefined,
): CvProcessingFailureKind | null {
  const breakdown = application?.cv_scores?.breakdown_json;
  const breakdownRecord = breakdown && typeof breakdown === 'object' && !Array.isArray(breakdown)
    ? breakdown as Record<string, unknown>
    : {};
  const persisted = readPersistedCvProcessingFailure(application?.cv_parsed_json);
  return classifyCvProcessingFailure({
    code: persisted.code,
    retryable: persisted.retryable,
    documentTypeValid: persisted.documentTypeValid ?? (breakdownRecord.document_type_valid === false ? false : null),
    applicationStatus: application?.status,
  });
}
